import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocCheckItem,
  DocListItem,
  DocSection,
  DocText,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  asClause,
  fillFrame,
  properWords,
  stripEnd,
  tidy,
} from "@/lib/tool-kit/essay/sentence";
import { purposePhrase } from "@/lib/tool-kit/essay/soapstone";
import {
  isFilled,
  itemOrPrompt,
  lettered,
  listItemOrPrompt,
} from "@/lib/tool-kit/lit-essays/common";

/**
 * An AP English Language-style rhetorical analysis outline.
 *
 * The AP Lang rhetorical analysis essay (free-response question 2) is scored out of six:
 * one point for a thesis that takes a position on the writer's rhetorical choices, up to
 * four for evidence and commentary, and one for sophistication (rubric effective Fall
 * 2019, College Board). The outline therefore holds three things apart that students
 * tend to blur — the claim about a choice, the textual evidence, and the commentary that
 * explains how the evidence supports the claim — and ends on the significance that the
 * sophistication point rewards.
 */

export type RhetoricalStrategy =
  | "ethos"
  | "pathos"
  | "logos"
  | "structure"
  | "diction"
  | "imagery"
  | "syntax"
  | "other";

export const RHETORICAL_STRATEGIES: readonly RhetoricalStrategy[] = [
  "ethos",
  "pathos",
  "logos",
  "structure",
  "diction",
  "imagery",
  "syntax",
  "other",
];

export interface RhetoricalParagraph {
  id: number;
  strategy: RhetoricalStrategy;
  claim: string;
  /** Quotations or references, one per line. */
  evidence: string;
  commentary: string;
}

export interface RhetoricalInput {
  title: string;
  author: string;
  occasion: string;
  audience: string;
  purpose: string;
  /** The rhetorical choices the essay will analyze, as a list. */
  choices: string;
  /** The opening sentence that places the passage: author, text, occasion. */
  context: string;
  summary: string;
  thesis: string;
  paragraphs: readonly RhetoricalParagraph[];
  significance: string;
}

export const MIN_PARAGRAPHS = 2;
export const MAX_PARAGRAPHS = 4;

export const RHETORICAL_THESIS_FRAMES = [
  "In {title}, {author} uses {choices} to {purpose}.",
  "To {purpose}, {author} relies on {choices} in {title}, adapting each appeal to {audience}.",
  "{author} builds {title} from {choices}, moving {audience} from ___ to ___.",
] as const;

export interface RhetoricalStrategyLabels {
  label: string;
  /** What the claim sentence should say, shown while the claim is empty. */
  claimPrompt: string;
  /** What the commentary must do for this strategy. */
  commentaryPrompt: string;
}

export interface RhetoricalLabels {
  docTitle: string;
  docTitleBlank: string;
  subtitle: string;
  subtitleBy: string;
  headings: {
    situation: string;
    introduction: string;
    body: string;
    conclusion: string;
    rubric: string;
  };
  fields: {
    author: string;
    occasion: string;
    audience: string;
    purpose: string;
  };
  items: {
    context: string;
    summary: string;
    thesis: string;
    claim: string;
    evidence: string;
    commentary: string;
    link: string;
    significance: string;
  };
  prompts: {
    context: string;
    summary: string;
    thesis: string;
    evidence: string;
    link: string;
    restate: string;
    significance: string;
  };
  strategies: Readonly<Record<RhetoricalStrategy, RhetoricalStrategyLabels>>;
  rubricNote: string;
  rubric: {
    thesis: string;
    evidence: string;
    commentary: string;
    choices: string;
    sophistication: string;
  };
  missing: Readonly<Record<string, string>>;
  thesisFrames?: readonly string[];
}

function orMissing(
  value: string,
  key: string,
  missing: Readonly<Record<string, string>>
): string {
  return value === "" ? (missing[key] ?? `[${key}]`) : value;
}

/** Thesis frames filled from the rhetorical situation; empty fields show as stand-ins. */
export function buildRhetoricalThesisFrames(
  input: RhetoricalInput,
  missing: Readonly<Record<string, string>>,
  frames: readonly string[] = RHETORICAL_THESIS_FRAMES
): string[] {
  const proper = properWords([
    input.title,
    input.author,
    input.audience,
    input.purpose,
    input.choices,
  ]);
  const values: Record<string, string> = {
    title: orMissing(stripEnd(input.title), "title", missing),
    author: orMissing(stripEnd(input.author), "author", missing),
    audience: orMissing(asClause(input.audience, proper), "audience", missing),
    purpose: orMissing(
      asClause(purposePhrase(input.purpose), proper),
      "purpose",
      missing
    ),
    choices: orMissing(asClause(input.choices, proper), "choices", missing),
  };
  return frames
    .map((frame) => fillFrame(frame, values))
    .filter((text): text is string => text !== null);
}

/** A body paragraph has everything the rubric's evidence-and-commentary row asks for. */
export function isParagraphComplete(paragraph: RhetoricalParagraph): boolean {
  return (
    isFilled(paragraph.claim) &&
    isFilled(paragraph.evidence) &&
    isFilled(paragraph.commentary)
  );
}

export interface RhetoricalChecks {
  thesis: boolean;
  evidence: boolean;
  commentary: boolean;
  choices: boolean;
  sophistication: boolean;
}

/** Planning checks against the rubric's rows. A prompt to revise, never a score. */
export function rhetoricalChecks(input: RhetoricalInput): RhetoricalChecks {
  const filled = input.paragraphs.filter((p) => isFilled(p.claim));
  const strategies = new Set(filled.map((p) => p.strategy));
  return {
    thesis: isFilled(input.thesis),
    evidence:
      filled.length >= MIN_PARAGRAPHS &&
      filled.every((p) => isFilled(p.evidence)),
    commentary:
      filled.length >= MIN_PARAGRAPHS &&
      filled.every((p) => isFilled(p.commentary)),
    choices: strategies.size >= MIN_PARAGRAPHS,
    sophistication: isFilled(input.significance),
  };
}

function situation(
  input: RhetoricalInput,
  labels: RhetoricalLabels
): DocSection {
  const field = (label: string, value: string) => ({
    label,
    value: tidy(value) === "" ? undefined : tidy(value),
  });
  return {
    heading: labels.headings.situation,
    blocks: [
      {
        kind: "fields",
        fields: [
          field(labels.fields.author, input.author),
          field(labels.fields.occasion, input.occasion),
          field(labels.fields.audience, input.audience),
          field(labels.fields.purpose, input.purpose),
        ],
      },
    ],
  };
}

function introduction(
  input: RhetoricalInput,
  labels: RhetoricalLabels
): DocSection {
  const thesisPrompt = labels.prompts.thesis;
  return {
    heading: labels.headings.introduction,
    blocks: [
      lettered([
        itemOrPrompt(
          labels.items.context,
          tidy(input.context),
          labels.prompts.context
        ),
        itemOrPrompt(
          labels.items.summary,
          tidy(input.summary),
          labels.prompts.summary
        ),
        itemOrPrompt(labels.items.thesis, tidy(input.thesis), thesisPrompt),
      ]),
    ],
  };
}

function bodySection(
  paragraph: RhetoricalParagraph,
  index: number,
  labels: RhetoricalLabels
): DocSection {
  const strategy = labels.strategies[paragraph.strategy];
  const claim: DocListItem = isFilled(paragraph.claim)
    ? itemOrPrompt(labels.items.claim, tidy(paragraph.claim), "")
    : { text: strategy.claimPrompt, prompt: true };
  const commentary: DocListItem = isFilled(paragraph.commentary)
    ? itemOrPrompt(labels.items.commentary, tidy(paragraph.commentary), "")
    : { text: strategy.commentaryPrompt, prompt: true };
  return {
    heading: t(labels.headings.body, {
      n: index + 1,
      strategy: strategy.label,
    }),
    blocks: [
      lettered([
        claim,
        listItemOrPrompt(
          labels.items.evidence,
          paragraph.evidence,
          labels.prompts.evidence
        ),
        commentary,
        { text: labels.prompts.link, prompt: true },
      ]),
    ],
  };
}

function rubricChecklist(
  input: RhetoricalInput,
  labels: RhetoricalLabels
): DocBlock {
  const checks = rhetoricalChecks(input);
  const items: DocCheckItem[] = [
    { text: labels.rubric.thesis, checked: checks.thesis },
    { text: labels.rubric.evidence, checked: checks.evidence },
    { text: labels.rubric.commentary, checked: checks.commentary },
    { text: labels.rubric.choices, checked: checks.choices },
    { text: labels.rubric.sophistication, checked: checks.sophistication },
  ];
  return { kind: "checklist", items };
}

/** The outline as a document: introduction, body paragraphs, conclusion, rubric check. */
export function buildRhetoricalOutline(
  input: RhetoricalInput,
  labels: RhetoricalLabels
): DocumentModel {
  const title = stripEnd(input.title);
  const author = stripEnd(input.author);
  let subtitle: DocText = labels.subtitle;
  if (author !== "") {
    subtitle = t(labels.subtitleBy, { author });
  }
  const sections: DocSection[] = [
    situation(input, labels),
    introduction(input, labels),
    ...input.paragraphs.map((paragraph, index) =>
      bodySection(paragraph, index, labels)
    ),
    {
      heading: labels.headings.conclusion,
      blocks: [
        lettered([
          { text: labels.prompts.restate, prompt: true },
          itemOrPrompt(
            labels.items.significance,
            tidy(input.significance),
            labels.prompts.significance
          ),
        ]),
      ],
    },
    {
      heading: labels.headings.rubric,
      blocks: [
        { kind: "guidance", text: labels.rubricNote },
        rubricChecklist(input, labels),
      ],
    },
  ];
  return {
    title: title === "" ? labels.docTitleBlank : t(labels.docTitle, { title }),
    subtitle,
    sections,
  };
}
